import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { ForgeProvider, createTheme } from 'wss3-forge'
// 'wss3-forge/styles' is the public bundle entry: body reset + font family +
// all CSS variables + motion + animations. Deep imports of just animations.css
// or motion.css give you ONLY keyframes — no typography/reset → unstyled body.
import 'wss3-forge/styles'
import './index.css'
import { App } from './App.js'
import { AuthProvider } from './contexts/AuthContext.js'
import { IncognitoProvider } from './contexts/IncognitoContext.js'
import { ChatProvider } from './contexts/ChatContext.js'
import { configureWebba } from './lib/webba.js'
import { queryClient } from './lib/query-client.js'

// Stark dark — restrained, near-black surfaces in the Claude.ai/Linear
// register. Forge's default dark uses #0F0F0F/#181818 already; we tighten the
// gradient between elevations and pull the borders down so cards read as
// outlined whitespace rather than tinted boxes. STARK violet (#7C65FF) stays
// the only chromatic accent.
const starkDark = createTheme(
  {
    brandPrimary: '#7C65FF',
    brandSecondary: '#A98BFF',
    bgPrimary: '#0E0E10',
    bgSecondary: '#161618',
    bgTertiary: '#1C1C1F',
    bgDropdown: '#1F1F23',
    bgElevated: '#222226',
    bgSubtle: 'rgba(255, 255, 255, 0.03)',
    textPrimary: '#ECECEC',
    textSecondary: '#B4B4B8',
    textMuted: '#7A7A82',
    borderColor: '#26262B',
    borderSubtle: '#1B1B1E',
  },
  'dark',
)

configureWebba()

if ('serviceWorker' in navigator) {
  // Fire-and-forget: a failed registration must not block the app from booting
  // (e.g. http:// in dev when not on localhost, or content-blocker on iOS).
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // intentionally swallowed
    })
  })
}

const rootElement = document.getElementById('app')
if (!rootElement) throw new Error('Mount node #app missing from index.html')

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ForgeProvider mode="dark" theme={starkDark}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <IncognitoProvider>
              <ChatProvider>
                <App />
              </ChatProvider>
            </IncognitoProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ForgeProvider>
  </React.StrictMode>,
)
